import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useAuth } from 'modules/authentication/presentation/contexts';
import { useCustomer } from 'modules/customer/presentation/hooks';
import {
  IWebchatIdentity,
  TWebchatWidgetStatus,
} from 'modules/webchat/domain/entities';
import {
  buildCustomerWebchatIdentity,
  parseVisitorWebchatIdentity,
  resolveWebchatLifecycleAction,
} from 'modules/webchat/domain/helpers';
import {
  makeLeaveWebchatUseCase,
  makeMountWebchatUseCase,
} from 'modules/webchat/domain/useCases';
import { makeWebchatWidgetProvider } from 'modules/webchat/infra/providers';
import {
  WEBCHAT_BLOCKED_ROUTES,
  WEBCHAT_ENVIRONMENT,
  WEBCHAT_OPENED_EVENT,
  WEBCHAT_SETTINGS,
} from 'modules/webchat/presentation/constants';
import {
  isBotBrowser,
  isWebchatBlockedRoute,
} from 'modules/webchat/presentation/helpers';
import IStorageProvider from 'shared/domain/providers/IStorageProvider';
import { makeStorageProvider } from 'shared/infra/providers';
import { useLocale, useRoute, useToast } from 'shared/presentation/contexts';
import { useEventTracker, useRegisterError } from 'shared/presentation/hooks';

import { IWebchatContextData, WebchatContext } from '../../hooks';

const readStoredVisitorIdentity = (
  storageProvider: IStorageProvider,
): IWebchatIdentity | null => {
  const stored: unknown = storageProvider.get('WEBCHAT_IDENTITY');
  const identity = parseVisitorWebchatIdentity(stored);

  // Valor editado no navegador ou de outra versão: some, e o visitante refaz o pré-chat.
  if (!identity && stored !== null) storageProvider.delete('WEBCHAT_IDENTITY');

  return identity;
};

const WIDGET_FAILURE_ERROR_KEY = 'ERRO_CARREGAR_WEBCHAT';

const DefaultWebchatProvider: React.FC = ({ children }) => {
  const { t, language } = useLocale('webchat');
  const { error: showErrorToast } = useToast();
  const { session, guestId } = useAuth();
  const { customer, loading: loadingCustomer } = useCustomer();
  const { pathname } = useRoute();
  const { register: registerEvent } = useEventTracker();
  const { register: registerError } = useRegisterError();

  const widgetProvider = useMemo(() => makeWebchatWidgetProvider(), []);
  const sessionStorageProvider = useMemo(
    () => makeStorageProvider({ type: 'SESSION' }),
    [],
  );

  const settings = WEBCHAT_SETTINGS[language];
  const isBot = useMemo(() => isBotBrowser(window.navigator), []);
  const canLoad = Boolean(settings) && !isBot;
  const isBlockedRoute = isWebchatBlockedRoute(
    pathname,
    WEBCHAT_BLOCKED_ROUTES,
  );

  const [visitorIdentity, setVisitorIdentity] =
    useState<IWebchatIdentity | null>(() =>
      session?.id ? null : readStoredVisitorIdentity(sessionStorageProvider),
    );
  const [mountedIdentifier, setMountedIdentifier] = useState<string | null>(
    null,
  );
  const [widgetStatus, setWidgetStatus] = useState<TWebchatWidgetStatus>(() =>
    widgetProvider.getStatus(),
  );
  const isResetting = useRef(false);
  const registerEventRef = useRef(registerEvent);
  const registerErrorRef = useRef(registerError);
  const isAuthenticated = Boolean(session?.id || guestId);
  const wasAuthenticated = useRef(isAuthenticated);

  // O widget lê o extra_params uma vez só: com sessão, monta depois de o cadastro (celular) carregar.
  const customerPhone = customer?.phone;
  const customerIdentity = useMemo(
    () =>
      session?.id && loadingCustomer
        ? null
        : buildCustomerWebchatIdentity(session, { phone: customerPhone }),
    [customerPhone, loadingCustomer, session],
  );

  const identity = customerIdentity ?? visitorIdentity;

  useEffect(() => {
    registerEventRef.current = registerEvent;
    registerErrorRef.current = registerError;
  }, [registerEvent, registerError]);

  useEffect(
    () =>
      widgetProvider.onStatusChange(status => {
        setWidgetStatus(status);

        if (status !== 'failed') return;

        const reason = widgetProvider.getFailureReason();

        registerErrorRef
          .current({
            key: WIDGET_FAILURE_ERROR_KEY,
            error: new Error(`O widget da Callbell não carregou: ${reason}`),
            payload: { motivo: reason },
          })
          .catch(() => undefined);
      }),
    [widgetProvider],
  );

  useEffect(
    () =>
      widgetProvider.onOpen(() =>
        registerEventRef.current(WEBCHAT_OPENED_EVENT),
      ),
    [widgetProvider],
  );

  useEffect(() => {
    if (!session?.id) return;

    // A identidade de visitante salva é de quem usou o navegador antes do login.
    sessionStorageProvider.delete('WEBCHAT_IDENTITY');
    setVisitorIdentity(null);
  }, [session?.id, sessionStorageProvider]);

  useEffect(() => {
    const loggedOut = wasAuthenticated.current && !isAuthenticated;

    wasAuthenticated.current = isAuthenticated;

    if (!loggedOut) return;

    // Quem entrar depois no mesmo navegador passa pelo pré-chat de novo.
    sessionStorageProvider.delete('WEBCHAT_IDENTITY');
    setVisitorIdentity(null);
  }, [isAuthenticated, sessionStorageProvider]);

  useEffect(() => {
    if (isResetting.current) return;

    const action = resolveWebchatLifecycleAction({
      canLoad,
      isBlockedRoute,
      identity,
      mountedIdentifier,
    });

    if (action === 'mount' && settings && identity) {
      makeMountWebchatUseCase().execute({
        settings,
        identity,
        language,
        environment: WEBCHAT_ENVIRONMENT,
      });
      setMountedIdentifier(identity.identifier);

      return;
    }

    if (action === 'show' || action === 'hide') {
      widgetProvider.setVisible(action === 'show');

      return;
    }

    if (action === 'reset') {
      isResetting.current = true;
      widgetProvider.reset();
    }
  }, [
    canLoad,
    identity,
    isBlockedRoute,
    language,
    mountedIdentifier,
    settings,
    widgetProvider,
  ]);

  const mountWebchat = useCallback<IWebchatContextData['mountWebchat']>(
    nextIdentity => {
      const visitorIdentity = parseVisitorWebchatIdentity(nextIdentity);

      if (session?.id || !visitorIdentity) return;

      try {
        sessionStorageProvider.store('WEBCHAT_IDENTITY', visitorIdentity);
      } catch {
        // Sem storage, a identidade vale só enquanto a página estiver aberta.
      }

      setVisitorIdentity(visitorIdentity);
    },
    [session?.id, sessionStorageProvider],
  );

  const leaveWebchat = useCallback(() => {
    // A sala e a identidade do pré-chat somem deste navegador: a próxima pessoa começa do zero.
    if (visitorIdentity) {
      makeLeaveWebchatUseCase().execute({
        identifier: visitorIdentity.identifier,
      });
    }

    sessionStorageProvider.delete('WEBCHAT_IDENTITY');
    setVisitorIdentity(null);
  }, [sessionStorageProvider, visitorIdentity]);

  useEffect(() => {
    // Só o visitante do pré-chat sai pelo chat; o cliente logado sai pelo logout do site.
    if (!canLoad || customerIdentity || !visitorIdentity) return undefined;

    widgetProvider.setExitAction({
      label: t('Leave'),
      title: t('Leave the chat'),
      personLabel: t('You are chatting as'),
      personName: visitorIdentity.name,
      onSelect: leaveWebchat,
    });

    return () => widgetProvider.setExitAction(null);
  }, [
    canLoad,
    customerIdentity,
    leaveWebchat,
    t,
    visitorIdentity,
    widgetProvider,
  ]);

  const openWebchat = useCallback<
    IWebchatContextData['openWebchat']
  >(async () => {
    const opened = await widgetProvider.open();

    if (!opened && widgetProvider.getStatus() === 'failed') {
      showErrorToast(
        t(
          'We could not open the chat now. Try again later or talk to us on WhatsApp',
        ),
      );
    }

    return opened;
  }, [showErrorToast, t, widgetProvider]);

  const value = useMemo<IWebchatContextData>(
    () => ({
      isEnabled: Boolean(settings),
      isAvailable: canLoad && !isBlockedRoute,
      identity,
      status: widgetStatus,
      isLauncherVisible:
        canLoad && !isBlockedRoute && widgetStatus !== 'failed',
      mountWebchat,
      openWebchat,
    }),
    [
      canLoad,
      identity,
      isBlockedRoute,
      mountWebchat,
      openWebchat,
      settings,
      widgetStatus,
    ],
  );

  return (
    <WebchatContext.Provider value={value}>{children}</WebchatContext.Provider>
  );
};

export default DefaultWebchatProvider;
